/*
  Magnum ink colour picker.

  The Infinite Options app drops a plain <select> into the product form for each
  colour slot ("Colour 1" ... "Colour 12"). Eighty colour names in a dropdown is
  hard work, so this script hides each select and puts a swatch button in its
  place: the colour dot, the colour name, and a searchable panel of every shade.

  Picking a colour writes straight back to the app's select and fires a change
  event, so the cart and the order are unaffected.

  The colours themselves live in magnum-colours.js, shared with the quick-add
  popup on collection pages. TO EDIT A COLOUR, change its hex code there.
*/
(function () {
  'use strict';

  var PLACEHOLDER = 'Select colour';

  var CARET =
    '<svg class="magnum-ink__caret" viewBox="0 0 10 6" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var openPicker = null;
  var counter = 0;

  function normalise(value) {
    var shared = colours();
    if (shared.normalise) return shared.normalise(value);
    return String(value).toLowerCase().replace(/[\s\-_]+/g, '');
  }

  /*
    Stock comes from the single bottle product ("1oz Radiant Colors Ink - All
    Colours"), which has one variant per colour. The theme writes that list into
    the page; a colour that is sold out there is greyed out here.
  */
  var stockList = null;

  function stock() {
    if (stockList) return stockList;
    stockList = {};
    var tag = document.getElementById('magnum-ink-stock');
    if (!tag) return stockList;
    var rows;
    try {
      rows = JSON.parse(tag.textContent);
    } catch (error) {
      return stockList;
    }
    rows.forEach(function (row) {
      if (row && row.name) stockList[normalise(row.name)] = row;
    });
    return stockList;
  }

  /* Colour names differ a little between the set and the bottles - the set says
     "Bubblegum" where the bottle is "Bubblegum Pink" - so fall back to a name
     that starts the same way. */
  function stockFor(value) {
    var rows = stock();
    var key = normalise(value);
    if (!key) return null;
    if (rows[key]) return rows[key];
    var names = Object.keys(rows);
    for (var i = 0; i < names.length; i++) {
      if (names[i].indexOf(key) === 0) return rows[names[i]];
    }
    return null;
  }

  function soldOut(value) {
    var row = stockFor(value);
    // A colour we cannot find a bottle for is left alone rather than hidden.
    return row ? row.inStock === false : false;
  }

  /* ---------- Working out a colour from its name ---------- */

  /*
    The colour list itself lives in magnum-colours.js, so the quick-add popup on
    collection pages shows the same colours as this picker. It is read late, in
    case that file happens to load after this one.
  */
  function colours() {
    return window.MagnumColours || {};
  }

  function swatchFor(value) {
    if (!value) return null;
    var shared = colours();
    return shared.hexFor ? shared.hexFor(value) : null;
  }

  function isMixed(value) {
    var shared = colours();
    return shared.isMixed ? shared.isMixed(value) : false;
  }
  /* The class for a dot with no colour of its own. */
  function plainDotClass(value) {
    return isMixed(value) ? ' magnum-ink__dot--mixed' : ' magnum-ink__dot--unknown';
  }

  /*
    The colour dot. A colour set in Shopify (an option swatch) is used as it
    comes; otherwise the hex for the name is used.
  */
  function dot(value, given) {
    var span = document.createElement('span');
    var hex = swatchFor(value);
    if (given && given.indexOf('url(') === 0) {
      span.className = 'magnum-ink__dot magnum-ink__dot--photo';
      span.style.backgroundImage = given;
      return span;
    }
    if (given) {
      span.className = 'magnum-ink__dot';
      span.style.setProperty('--mi-swatch', given);
      return span;
    }
    span.className = 'magnum-ink__dot' + (hex ? '' : plainDotClass(value));
    if (hex) span.style.setProperty('--mi-swatch', hex);
    return span;
  }

  /*
    Is this one of the theme's own colour dropdowns? Those are the Shopify
    variant dropdowns on a single product - "Colour" on a bottle of ink - and
    they are plain lists of names with no colour to look at.
  */
  function isVariantSelect(select) {
    if (select.dataset.magnumInk) return false;
    if (select.multiple || select.options.length < 2) return false;
    if (!select.closest('variant-selects')) return false;
    var name = String(select.name || '').toLowerCase();
    return name.indexOf('colour') !== -1 || name.indexOf('color') !== -1 || name.indexOf('skin tone') !== -1;
  }

  /* A colour set on the option in Shopify, if there is one. */
  function givenSwatch(option) {
    return (option && option.getAttribute('data-option-swatch-value')) || '';
  }

  /* The theme writes "Deep Red - Unavailable" for a colour that is sold out. */
  function optionUnavailable(option) {
    return String(option.text).trim() !== String(option.value).trim();
  }

  /* Is this one of the app's colour dropdowns? */
  function isColourSelect(select) {
    if (select.dataset.magnumInk) return false;
    if (select.multiple || select.options.length < 6) return false;
    if (!select.closest('#infiniteoptions-container')) return false;
    // Only take over dropdowns where most of the values are known ink names.
    var known = 0;
    for (var i = 0; i < select.options.length; i++) {
      if (swatchFor(select.options[i].value)) known++;
    }
    return known >= select.options.length / 2;
  }

  /*
    Show the colour that was just picked in the main product photo. The bottle
    photos come from the single bottle product, alongside its stock. The pack
    shot is kept so the gallery can be put back.
  */
  function showColourPhoto(value) {
    var row = stockFor(value);
    var picture = row && row.image;
    if (!picture) return;

    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    var slide = gallery.querySelector('.product__media-item');
    if (!slide) return;
    var image = slide.querySelector('img');
    if (!image) return;

    if (image.dataset.magnumPackShot === undefined) {
      image.dataset.magnumPackShot = image.getAttribute('src') || '';
      image.dataset.magnumPackShotSet = image.getAttribute('srcset') || '';
    }
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.setAttribute('src', picture);

    // The thumbnail under the photo has to follow, or the two disagree.
    var mediaId = slide.dataset.mediaId;
    var thumb = mediaId && gallery.querySelector('[data-target="' + mediaId + '"] img');
    if (thumb) {
      thumb.removeAttribute('srcset');
      thumb.removeAttribute('sizes');
      thumb.setAttribute('src', picture);
    }

    if (typeof gallery.setActiveMedia === 'function' && mediaId) {
      gallery.setActiveMedia(mediaId, false);
    }
  }

  function closeOpen(exceptPicker) {
    if (openPicker && openPicker !== exceptPicker) openPicker.close();
  }

  function build(select, variant) {
    select.dataset.magnumInk = 'true';

    /*
      The app's dropdown opens on its first colour, so a customer who never
      touches it would quietly order twelve of the same ink. Put an empty
      choice at the top and start there instead - the pill reads "Select
      colour" until they pick, and the basket is blocked while any is empty.
      A variant dropdown always has a colour chosen already, so it is left be.
    */
    if (!variant) {
      var blank = document.createElement('option');
      blank.value = '';
      blank.textContent = PLACEHOLDER;
      select.insertBefore(blank, select.firstChild);
      select.value = '';
    }

    var id = 'magnum-ink-' + ++counter;
    var wrapper = document.createElement('div');
    wrapper.className = 'magnum-ink' + (variant ? ' magnum-ink--variant' : '');

    select.parentNode.insertBefore(wrapper, select);
    wrapper.appendChild(select);
    select.classList.add('magnum-ink__native');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'magnum-ink__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.id = id + '-button';
    var slotName = labelTextFor(select);
    if (slotName) button.setAttribute('aria-label', slotName);

    var buttonDot = dot(select.value, givenSwatch(select.options[select.selectedIndex]));
    var buttonName = document.createElement('span');
    buttonName.className = 'magnum-ink__name';
    buttonName.textContent = select.value;

    button.appendChild(buttonDot);
    button.appendChild(buttonName);
    button.insertAdjacentHTML('beforeend', CARET);
    wrapper.appendChild(button);

    var panel = document.createElement('div');
    panel.className = 'magnum-ink__panel';
    panel.hidden = true;

    // A short list is quicker to read than to search.
    var searchable = select.options.length > 12;

    var search = document.createElement('input');
    search.type = 'search';
    search.className = 'magnum-ink__search';
    search.placeholder = 'Search colours';
    search.setAttribute('aria-label', 'Search colours');
    search.hidden = !searchable;
    panel.appendChild(search);

    var list = document.createElement('ul');
    list.className = 'magnum-ink__list';
    list.setAttribute('role', 'listbox');
    list.id = id + '-list';
    panel.appendChild(list);

    var empty = document.createElement('p');
    empty.className = 'magnum-ink__empty';
    empty.textContent = 'No colours match that search.';
    empty.hidden = true;
    panel.appendChild(empty);

    var options = [];
    Array.prototype.forEach.call(select.options, function (option) {
      if (option.value === '') return;
      var item = document.createElement('li');
      var choice = document.createElement('button');
      choice.type = 'button';
      choice.className = 'magnum-ink__option';
      choice.setAttribute('role', 'option');
      choice.dataset.value = option.value;
      choice.dataset.search = normalise(option.text);
      choice.setAttribute('aria-selected', option.value === select.value ? 'true' : 'false');
      choice.appendChild(dot(option.value, givenSwatch(option)));

      // Name and any note sit in a column of their own, so a long name and the
      // words "Out of stock" never squeeze each other along one line.
      var label = document.createElement('span');
      label.className = 'magnum-ink__label';
      var name = document.createElement('span');
      name.className = 'magnum-ink__optionname';
      name.textContent = variant ? String(option.value).trim() : String(option.text).trim();
      name.title = name.textContent;
      label.appendChild(name);

      // A sold out colour in a set cannot be ordered at all; a sold out
      // variant can still be opened, so it is greyed but left clickable.
      var gone = variant ? optionUnavailable(option) : soldOut(option.value);
      if (gone) {
        if (!variant) {
          choice.disabled = true;
          choice.setAttribute('aria-disabled', 'true');
        }
        choice.classList.add('magnum-ink__option--gone');
        var tag = document.createElement('span');
        tag.className = 'magnum-ink__gone';
        tag.textContent = 'Out of stock';
        label.appendChild(tag);
      }

      choice.appendChild(label);

      item.appendChild(choice);
      list.appendChild(item);
      options.push(choice);

      choice.addEventListener('click', function () {
        if (gone && !variant) return;
        select.value = option.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        if (!variant) showColourPhoto(option.value);
        picker.close();
        button.focus();
      });
    });

    // Ten colours or fewer read better as one list; longer lists go in two
    // columns so the panel does not run off the bottom of the screen.
    if (options.length <= 10) list.classList.add('magnum-ink__list--single');
    wrapper.dataset.columns = options.length <= 10 ? '1' : '2';

    panel.hidden = true;
    wrapper.appendChild(panel);

    // The theme draws its own arrow beside the dropdown; ours replaces it.
    if (variant) {
      var box = wrapper.closest('.select');
      if (box) box.classList.add('magnum-ink-select');
    }

    function sync() {
      var value = select.value;
      var given = givenSwatch(select.options[select.selectedIndex]);
      var photo = given.indexOf('url(') === 0;
      var hex = photo ? null : given || swatchFor(value);
      var spare = value === '' ? ' magnum-ink__dot--empty' : photo ? ' magnum-ink__dot--photo' : hex ? '' : plainDotClass(value);
      buttonDot.className = 'magnum-ink__dot' + spare;
      buttonDot.style.backgroundImage = photo ? given : '';
      buttonDot.style.setProperty('--mi-swatch', hex || '');
      buttonName.textContent = value === '' ? PLACEHOLDER : value;
      wrapper.dataset.chosen = value === '' ? 'false' : 'true';
      if (value !== '') wrapper.removeAttribute('data-missing');
      options.forEach(function (choice) {
        choice.setAttribute('aria-selected', choice.dataset.value === value ? 'true' : 'false');
      });
    }

    function filter() {
      var term = normalise(search.value);
      var shown = 0;
      options.forEach(function (choice) {
        var match = !term || choice.dataset.search.indexOf(term) !== -1;
        choice.parentNode.hidden = !match;
        if (match) shown++;
      });
      empty.hidden = shown > 0;
    }

    var picker = {
      close: function () {
        if (panel.hidden) return;
        panel.hidden = true;
        wrapper.dataset.open = 'false';
        button.setAttribute('aria-expanded', 'false');
        if (openPicker === picker) openPicker = null;
      },
      open: function () {
        closeOpen(picker);
        panel.hidden = false;
        wrapper.dataset.open = 'true';
        button.setAttribute('aria-expanded', 'true');
        openPicker = picker;
        search.value = '';
        filter();
        var selected = options.filter(function (choice) {
          return choice.getAttribute('aria-selected') === 'true';
        })[0];
        if (selected) selected.scrollIntoView({ block: 'nearest' });
        // The pill is narrower than the panel, so nudge the panel sideways until
        // it sits fully on screen whichever pill it belongs to.
        // Start under the pill, then shuffle sideways until the panel is fully
        // on screen. The page is scaled on dense screens and the panel's width
        // settles as it moves, so nudge and re-measure rather than trusting one
        // sum.
        panel.style.left = '0px';
        var room = document.documentElement.clientWidth;
        var edge = 12;
        for (var tries = 0; tries < 4; tries++) {
          var box = panel.getBoundingClientRect();
          var out = 0;
          if (box.right > room - edge) out = box.right - (room - edge);
          if (box.left - out < edge) out = box.left - edge;
          if (Math.abs(out) < 0.5) break;
          panel.style.left = (parseFloat(panel.style.left) || 0) - out + 'px';
        }
        search.focus();
      },
      contains: function (node) {
        return wrapper.contains(node);
      },
      focusButton: function () {
        button.focus();
      },
    };

    button.addEventListener('click', function () {
      if (panel.hidden) picker.open();
      else picker.close();
    });

    search.addEventListener('input', filter);

    panel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        picker.close();
        button.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      var visible = options.filter(function (choice) {
        return !choice.parentNode.hidden && !choice.disabled;
      });
      if (!visible.length) return;
      var index = visible.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') index = index < 0 ? 0 : Math.min(index + 1, visible.length - 1);
      else if (index <= 0) {
        search.focus();
        return;
      } else index -= 1;
      visible[index].focus();
    });

    // The app repopulates its selects on some products; keep in step with it.
    select.addEventListener('change', sync);

    sync();
  }

  /* The app's own "Colour 1" style label, used for the button's screen-reader name. */
  function labelTextFor(select) {
    if (!select.id) return '';
    var label = null;
    try {
      label = document.querySelector('label[for="' + CSS.escape(select.id) + '"]');
    } catch (error) {
      label = null;
    }
    return label ? label.textContent.trim() : '';
  }

  /* Mark up the app's own wrappers so the colour slots can sit as pills in a row. */
  function markSlots() {
    var container = document.querySelector('#infiniteoptions-container');
    if (!container) return;
    var pills = container.querySelectorAll('.magnum-ink');
    if (!pills.length) return;
    container.classList.add('magnum-ink-slots');
    Array.prototype.forEach.call(pills, function (wrapper) {
      var slot = wrapper;
      // Walk up to the app's own row for this option and flag it.
      while (slot.parentNode && slot.parentNode !== container) slot = slot.parentNode;
      if (slot.parentNode === container) slot.classList.add('magnum-ink-slot');
    });
    widenSlots(container);
  }

  /*
    The product form shares its row with the quantity box, so the options only
    get part of the width - fine for one dropdown per line, too tight for pills.
    The quantity box sits at the bottom of that row, so the space beside the
    pills is empty: pull them across it and they get the whole row.
  */
  function widenSlots(container) {
    var row = container.closest('.magnum-buy-row');
    if (!row) return;
    var beside = null;
    Array.prototype.forEach.call(row.children, function (child) {
      if (!beside && !child.contains(container)) beside = child;
    });
    if (!beside) return;
    row.classList.add('magnum-ink-buyrow');
    var gap = parseFloat(window.getComputedStyle(row).columnGap) || 0;
    var width = beside.getBoundingClientRect().width;
    // Measured before the quantity box has been laid out, this comes back as
    // nothing and the pills stay squeezed. Leave it be and try again later.
    if (width < 40) return;
    container.style.setProperty('--mi-pull', width + gap + 'px');
  }

  function rewiden() {
    var container = document.querySelector('#infiniteoptions-container.magnum-ink-slots');
    if (container) widenSlots(container);
  }

  function enhance() {
    var selects = document.querySelectorAll('#infiniteoptions-container select');
    Array.prototype.forEach.call(selects, function (select) {
      if (isColourSelect(select)) build(select);
    });
    // The theme's own colour dropdown, on the single bottles and anything else
    // sold by colour.
    var theme = document.querySelectorAll('variant-selects select');
    Array.prototype.forEach.call(theme, function (select) {
      if (isVariantSelect(select)) build(select, true);
    });
    markSlots();
    colourPillDots();
  }

  /*
    Short colour lists on the product page are pills, not a dropdown. A pill
    whose colour is not set in Shopify gets its dot from the colour's name.
  */
  function colourPillDots() {
    var dots = document.querySelectorAll('.magnum-pill__dot[data-magnum-colour]');
    Array.prototype.forEach.call(dots, function (span) {
      var made = dot(span.getAttribute('data-magnum-colour'));
      made.classList.add('magnum-pill__dot');
      made.setAttribute('aria-hidden', 'true');
      span.replaceWith(made);
    });
  }

  document.addEventListener('click', function (event) {
    if (openPicker && !openPicker.contains(event.target)) openPicker.close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openPicker) {
      var picker = openPicker;
      picker.close();
      picker.focusButton();
    }
  });

  /* ---------- Nothing goes in the basket until every colour is picked ---------- */

  function warningFor(form) {
    var note = form.querySelector('.magnum-ink-warning');
    if (note) return note;
    note = document.createElement('p');
    note.className = 'magnum-ink-warning';
    note.setAttribute('role', 'alert');
    note.hidden = true;
    var slots = form.querySelector('.magnum-ink-slots');
    if (slots && slots.parentNode) slots.parentNode.insertBefore(note, slots.nextSibling);
    else form.insertBefore(note, form.firstChild);
    return note;
  }

  function emptySlotsIn(form) {
    var empty = [];
    var selects = form.querySelectorAll('select[data-magnum-ink="true"]');
    Array.prototype.forEach.call(selects, function (select) {
      if (select.value !== '') return;
      var wrapper = select.closest('.magnum-ink');
      if (wrapper) empty.push(wrapper);
    });
    return empty;
  }

  // On the way down, so this runs before the theme's own add to basket code.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.querySelector) return;
      if (!form.querySelector('select[data-magnum-ink="true"]')) return;

      var empty = emptySlotsIn(form);
      var note = warningFor(form);
      if (!empty.length) {
        note.hidden = true;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      empty.forEach(function (wrapper) {
        wrapper.dataset.missing = 'true';
      });
      note.textContent =
        empty.length === 1
          ? 'Please pick your last colour.'
          : 'Please pick all ' + empty.length + ' remaining colours.';
      note.hidden = false;
      var first = empty[0].querySelector('.magnum-ink__button');
      if (first) {
        first.scrollIntoView({ block: 'center' });
        first.focus();
      }
    },
    true
  );

  window.addEventListener('resize', function () {
    window.clearTimeout(rewiden._timer);
    rewiden._timer = window.setTimeout(rewiden, 120);
  });

  // Fonts and the quantity box settle after the app has drawn its dropdowns,
  // so measure the width again once the page has finished loading.
  window.addEventListener('load', function () {
    rewiden();
    window.setTimeout(rewiden, 400);
  });

  function start() {
    enhance();
    // Infinite Options injects its fields after the page loads, and again when
    // the variant changes, so keep watching for new dropdowns.
    var observer = new MutationObserver(function () {
      window.clearTimeout(start._timer);
      start._timer = window.setTimeout(enhance, 60);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
